import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { FaExclamationTriangle } from "react-icons/fa";
import { MdSchool } from "react-icons/md";

/* ────────────────────────────────────────────────────────── */
/* Theme                                                      */
/* ────────────────────────────────────────────────────────── */
const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  success: "#16a34a",
  warn: "#b91c1c",
};

/* ────────────────────────────────────────────────────────── */
/* Page                                                       */
/* ────────────────────────────────────────────────────────── */
export default function Mentors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [accessDenied, setAccessDenied] = useState(false);

  const [toast, setToast] = useState("");
  const [changing, setChanging] = useState({}); // { [id]: boolean }

  useEffect(() => {
    // Permission: allow if super_admin or has 'view_mentors'
    const user = JSON.parse(localStorage.getItem("employee"));
    const isSuperAdmin = user?.role === "super_admin";
    const canView = isSuperAdmin || (user?.permissions || []).includes("view_mentors");
    if (!canView) {
      setAccessDenied(true);
      return;
    }

    const q = query(collection(db, "mentors"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const x = d.data() || {};
          return {
            id: d.id,
            name: x.name || "—",
            category: x.category || "—",
            modes: Array.isArray(x.modes) ? x.modes : [],
            chatRate: x.chatRate || "",
            audioRate: x.audioRate || "",
            videoRate: x.videoRate || "",
            profileImage: x.profileImage || "",
            status: String(x.status || "pending").toLowerCase(),
            isFeatured: !!x.isFeatured, // <- NEW FIELD
            createdAt: x.createdAt || null,
          };
        });
        setRows(list);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setErr("Failed to load mentors.");
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const pending = useMemo(() => rows.filter((r) => r.status === "pending"), [rows]);
  const approved = useMemo(() => rows.filter((r) => r.status === "approved"), [rows]);
  const featuredCount = useMemo(() => rows.filter((r) => r.isFeatured).length, [rows]);

  async function applyStatus(id, newStatus, isFeatured) {
    try {
      setChanging((m) => ({ ...m, [id]: true }));
      await updateDoc(doc(db, "mentors", id), {
        status: String(newStatus || "pending").toLowerCase(),
        isFeatured: !!isFeatured,
      });
      setToast("Status updated successfully.");
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      console.error(e);
      setToast("Update failed. Try again.");
      setTimeout(() => setToast(""), 2500);
    } finally {
      setChanging((m) => ({ ...m, [id]: false }));
    }
  }

  if (accessDenied) {
    return (
      <div style={deniedWrap}>
        <div style={deniedCard}>
          <FaExclamationTriangle size={28} color="#e53935" />
          <h3 style={{ margin: "0.5rem 0 0" }}>Access Denied</h3>
          <p style={{ marginTop: 6, color: COLORS.subtext }}>
            You do not have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-pro-page">
      <div className="aa-pro-hero" style={{ marginBottom: 18, display: "grid", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="aa-pro-hero-icon">
            <MdSchool />
          </div>
          <div>
            <h2 className="aa-pro-title" style={{ marginBottom: 6 }}>Mentors</h2>
            <p className="aa-pro-subtitle" style={{ margin: 0 }}>
              Review mentor applications, approve profiles, and manage featured mentors in one place.
            </p>
          </div>
        </div>
        <div
          className="aa-pro-metrics"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, minWidth: 280 }}
        >
          <Metric label="Total" value={rows.length} />
          <Metric label="Pending" value={pending.length} />
          <Metric label="Approved" value={approved.length} />
          <Metric label="Featured" value={featuredCount} />
        </div>
      </div>

      {loading && <div style={{ color: COLORS.subtext }}>Loading…</div>}
      {err && <div style={{ color: COLORS.warn }}>{err}</div>}

      {!loading && !err && (
        <>
          <Section title={`Pending (${pending.length})`}>
            <MentorsTable
              rows={pending}
              onApply={applyStatus}
              changing={changing}
            />
          </Section>

          <Section title={`Approved (${approved.length})`} topGap>
            <MentorsTable
              rows={approved}
              onApply={applyStatus}
              changing={changing}
            />
          </Section>
        </>
      )}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 16,
            right: 16,
            background: "#fff",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 8,
            padding: "10px 12px",
            boxShadow: "0 6px 20px rgba(0,0,0,.12)",
            color: COLORS.text,
            fontWeight: 700,
            zIndex: 60,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Parts                                                      */
/* ────────────────────────────────────────────────────────── */
function Section({ title, children, topGap }) {
  return (
    <div style={{ marginTop: topGap ? 18 : 0 }}>
      <h3 style={{ margin: "10px 0", color: COLORS.text }}>{title}</h3>
      <div className="aa-pro-table-shell" style={{ overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="aa-pro-metric">
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "rgba(255,255,255,0.72)" }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "#ffffff" }}>{value}</div>
    </div>
  );
}

function MentorsTable({ rows, onApply, changing }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
      <thead>
        <tr style={{ background: "#f8fafc", borderBottom: `1px solid ${COLORS.border}` }}>
          <Th>Mentor</Th>
          <Th>Category</Th>
          <Th>Modes</Th>
          <Th>Rates</Th>
          <Th>Created</Th>
          <Th>Featured</Th>
          <Th>Status</Th>
          <Th>Update</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <Td>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {r.profileImage ? (
                  <img
                    src={r.profileImage}
                    alt={r.name}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: `1px solid ${COLORS.border}`,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: "#fff",
                      border: `1px solid ${COLORS.border}`,
                    }}
                  />
                )}
                <div style={{ fontWeight: 700 }}>{r.name}</div>
              </div>
            </Td>
            <Td>{r.category}</Td>
            <Td>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(r.modes || []).map((m, i) => (
                  <span key={i} style={chip}>{m}</span>
                ))}
                {(!r.modes || r.modes.length === 0) && <span style={{ color: COLORS.subtext }}>—</span>}
              </div>
            </Td>
            <Td>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {r.chatRate ? <span style={rateBadge}>Chat ₹{r.chatRate}</span> : null}
                {r.audioRate ? <span style={rateBadge}>Audio ₹{r.audioRate}</span> : null}
                {r.videoRate ? <span style={rateBadge}>Video ₹{r.videoRate}</span> : null}
                {!r.chatRate && !r.audioRate && !r.videoRate && (
                  <span style={{ color: COLORS.subtext }}>—</span>
                )}
              </div>
            </Td>
            <Td>{formatDate(r.createdAt)}</Td>
            <Td>
              <FeaturedSelect id={r.id} initial={r.isFeatured} />
            </Td>
            <Td>
              <StatusSelect id={r.id} initial={r.status} />
            </Td>
            <Td>
              <ApplyButton id={r.id} onApply={onApply} changing={!!changing[r.id]} />
            </Td>
          </tr>
        ))}
        {rows.length === 0 && (
          <tr>
            <td colSpan={8} style={{ padding: 14, color: COLORS.subtext }}>
              No records.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Th({ children }) {
  return (
    <th style={{ textAlign: "left", padding: 10, fontWeight: 800, color: COLORS.text }}>
      {children}
    </th>
  );
}
function Td({ children }) {
  return <td style={{ padding: 10, color: COLORS.text, verticalAlign: "top" }}>{children}</td>;
}

function StatusSelect({ id, initial }) {
  const [val, setVal] = useState(String(initial || "pending").toLowerCase());
  return (
    <select
      data-id={id}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      style={selectStyle}
      id={`mentor-status-${id}`}
    >
      <option value="pending">Pending</option>
      <option value="approved">Approved</option>
    </select>
  );
}

function FeaturedSelect({ id, initial }) {
  const [val, setVal] = useState(initial ? "true" : "false");
  return (
    <select
      data-id={id}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      style={selectStyle}
      id={`mentor-featured-${id}`}
    >
      <option value="false">False</option>
      <option value="true">True</option>
    </select>
  );
}

function ApplyButton({ id, onApply, changing }) {
  return (
    <button
      style={primaryBtn}
      disabled={changing}
      onClick={() => {
        const statusEl = document.getElementById(`mentor-status-${id}`);
        const featuredEl = document.getElementById(`mentor-featured-${id}`);
        const nextStatus = statusEl ? statusEl.value : "pending";
        const nextFeatured = featuredEl ? featuredEl.value === "true" : false;
        onApply(id, nextStatus, nextFeatured);
      }}
    >
      {changing ? "Updating…" : "Update"}
    </button>
  );
}

/* ────────────────────────────────────────────────────────── */
/* Styles + helpers                                           */
/* ────────────────────────────────────────────────────────── */
const chip = {
  padding: "4px 8px",
  borderRadius: 999,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  fontSize: 12,
};

const rateBadge = {
  padding: "4px 8px",
  borderRadius: 8,
  background: "#f8fafc",
  border: `1px solid ${COLORS.border}`,
  fontSize: 12,
  fontWeight: 700,
};

const primaryBtn = {
  height: 34,
  padding: "0 12px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const selectStyle = {
  height: 34,
  padding: "0 10px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
};

const deniedWrap = {
  backdropFilter: "blur(6px)",
  backgroundColor: "rgba(0,0,0,0.4)",
  width: "100vw",
  height: "100vh",
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const deniedCard = {
  background: "#fff",
  padding: "2rem",
  borderRadius: "10px",
  width: "90%",
  maxWidth: "420px",
  textAlign: "center",
  boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
};

function formatDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}
